import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Lock, LogIn, Mail, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false,
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleForgotPassword = () => {
    toast.info('Password reset is coming soon. Please contact the administrator.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', {
        email: formData.email,
        password: formData.password,
      });

      if (response.data?.token) {
        const userData = response.data.user;
        localStorage.setItem('token', response.data.token);
        login(userData);
        toast.success(`Welcome back, ${userData.name}!`);
        navigate('/admin');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error details:', error);
      if (error.response) {
        toast.error(error.response.data?.message || 'Invalid credentials');
      } else if (error.request) {
        toast.error('Cannot connect to server. Please check if the backend is running.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel */}
      <aside className="relative hidden overflow-hidden bg-navy-fade lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
          aria-hidden="true"
        />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-2xs font-bold uppercase tracking-widest text-gold-300">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Staff access
          </span>
          <h2 className="mt-6 text-3xl font-extrabold leading-tight text-white">
            Graduation
            <span className="block text-gold-400">Management System</span>
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-white/70">
            Manage graduate records, gown requests, booklets, alumni nominations and
            ceremony announcements from one place.
          </p>
        </div>

        <ul className="relative space-y-3 text-sm text-white/80">
          {[
            'Verify and publish the official graduation list',
            'Track gown requests and approvals in real time',
            'Publish ceremony updates to the public site',
          ].map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span
                className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gold-400/20"
                aria-hidden="true"
              >
                <ShieldCheck className="h-3 w-3 text-gold-300" />
              </span>
              {item}
            </li>
          ))}
        </ul>

        <p className="relative text-xs text-white/40">
          © {new Date().getFullYear()} Katine Technical Training Institute
        </p>
      </aside>

      {/* Form panel */}
      <div className="flex items-center justify-center bg-ink-50 px-4 py-12 sm:px-6">
        <div className="w-full max-w-md">
          {/* Compact brand mark for small screens (the brand panel is hidden there) */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-brand-700 p-1">
              <img src="/images/logo.png" alt="" className="h-full w-full object-contain" />
            </span>
            <div>
              <p className="text-sm font-bold leading-tight text-ink-900">Katine TTI</p>
              <p className="text-2xs uppercase tracking-wider text-brand-600">
                Graduation Management
              </p>
            </div>
          </div>

          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-1.5 text-sm font-medium text-ink-500 transition-colors hover:text-brand-700"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to public site
          </Link>

          <h1 className="text-2xl font-bold text-ink-900">Sign in to your account</h1>
          <p className="mt-1.5 text-sm text-ink-500">
            Staff and administrator access to the graduation system.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="email" className="label">
                Email address
              </label>
              <div className="relative">
                <Mail
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@katinetti.ac.ke"
                  autoComplete="email"
                  required
                  className="input-field pl-9"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="label">
                  Password
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="mb-1.5 text-xs font-semibold text-brand-700 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  className="input-field pl-9"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-ink-600">
              <input
                type="checkbox"
                name="remember"
                checked={formData.remember}
                onChange={handleChange}
                className="h-4 w-4 rounded border-ink-300 text-brand-700 focus:ring-brand-500"
              />
              Remember me on this device
            </label>

            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Signing in…
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" aria-hidden="true" />
                  Sign in
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-ink-500">
            Are you a graduate?{' '}
            <Link to="/search" className="font-semibold text-brand-700 hover:underline">
              Verify your graduation status
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
